/**
 * @fileoverview Hook to fetch linked payments count for multiple indirect costs.
 * @module features/indirect-costs/hooks/useLinkedPaymentsCount
 */

import { useQueries } from '@tanstack/react-query'
import { fetchLinkedPayments } from '../api/indirectCostsLinksService'

/**
 * Hook to fetch linked payments count for multiple indirect costs.
 *
 * @param costUuids Array of indirect cost UUIDs
 * @returns Object mapping cost UUIDs to their linked payments count
 */
export function useLinkedPaymentsCount(costUuids: string[]): {
  counts: Record<
    string,
    { count: number; hasLinkedPayments: boolean; isLoading: boolean; error: unknown }
  >
  isLoading: boolean
  error: unknown
} {
  const queries = useQueries({
    queries: costUuids.map(costUuid => ({
      queryKey: ['indirect-costs', 'linked-payments-count', costUuid],
      queryFn: async () => {
        const result = await fetchLinkedPayments(costUuid)
        return {
          costUuid,
          count: result.payments.length,
          hasLinkedPayments: result.payments.length > 0,
        }
      },
      enabled: Boolean(costUuid),
      staleTime: 5 * 60 * 1000, // 5 minutes
    })),
  })

  const counts = queries.reduce(
    (acc, query) => {
      if (query.data) {
        acc[query.data.costUuid] = {
          count: query.data.count,
          hasLinkedPayments: query.data.hasLinkedPayments,
          isLoading: query.isLoading,
          error: query.error,
        }
      }
      return acc
    },
    {} as Record<
      string,
      { count: number; hasLinkedPayments: boolean; isLoading: boolean; error: unknown }
    >
  )

  return {
    counts,
    isLoading: queries.some(q => q.isLoading),
    error: queries.find(q => q.error)?.error,
  }
}
